import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Clock, History, XCircle, CheckCircle2, Info, X, Trophy, Flame, ExternalLink } from "lucide-react";
import { getDashboardStats, getPools, getTopDepositors } from "@/lib/api";
import { useWallet } from "@/context/WalletContext";
import SectionLabel from "@/components/SectionLabel";
import { ONCHAIN } from "@/config";

const usd = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n || 0);
const usd2 = (n) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

function fmtCountdown(secs) {
  if (secs == null || secs <= 0) return "00:00:00";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  const pad = (x) => String(x).padStart(2, "0");
  return d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function fmtOpenAt(iso) {  // eslint-disable-line no-unused-vars
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + " UTC";
  } catch { return ""; }
}

const shortA = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "—");

function TopDepositorCard({ data, now }) {
  const top3 = data?.top3 || [];
  const top = data?.top || [];
  const pct = [50, 30, 20];
  const secsLeft = data?.settle_at ? Math.max(0, Math.floor((new Date(data.settle_at).getTime() - now) / 1000)) : null;
  return (
    <div data-testid="pool-top-depositor" className="card-glow p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-lg font-bold grad-title">Top Depositor Reward Pool</div>
        </div>
        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-[#FFA000]/45 bg-[#FFA000]/12 px-2.5 py-1 text-[10px] font-semibold text-[#FFC24B]">
          <Flame size={11} /> 24h Reward
        </span>
      </div>

      <div className="mt-3 text-[11px] uppercase tracking-wide text-white/45">Current Pool Balance</div>
      <div className="text-3xl font-extrabold text-[#34D07A]">${usd2(data?.pool_balance)}</div>

      <div className="mt-3 rounded-xl border border-[#3C6B33]/50 p-3">
        <div className="mb-1 flex items-center gap-1.5 text-sm font-bold text-white">
          <Trophy size={14} className="text-[#D6C51E]" /> Top 3 share the pool
        </div>
        {[0, 1, 2].map((i) => {
          const t = top3[i];
          return (
            <div key={i} data-testid={`top-depositor-rank-${i + 1}`} className="flex items-center justify-between py-1 text-xs">
              {t?.address ? (
                <a
                  href={t.tx_hash ? `${ONCHAIN.explorer}/tx/${t.tx_hash}` : `${ONCHAIN.explorer}/address/${t.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`top-depositor-rank-${i + 1}-link`}
                  className="flex min-w-0 items-center gap-1.5 text-white/60 hover:text-[#34D07A]"
                >
                  <span className="text-base leading-none">{["🥇", "🥈", "🥉"][i]}</span>
                  <span className="truncate font-mono text-white/70 underline decoration-dotted underline-offset-2">{shortA(t.address)}</span>
                  <ExternalLink size={11} className="shrink-0 text-[#34D07A]" />
                </a>
              ) : (
                <span className="flex min-w-0 items-center gap-1.5 text-white/60">
                  <span className="text-base leading-none">{["🥇", "🥈", "🥉"][i]}</span>
                  <span className="truncate font-mono text-white/70">—</span>
                </span>
              )}
              <div className="flex shrink-0 items-center gap-2">
                <span className="w-9 text-right text-xs font-semibold text-white/50">{pct[i]}%</span>
                <b className="w-16 text-right text-[#34D07A]">${usd2(t?.reward || 0)}</b>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex w-full items-center justify-between rounded-lg border border-[#D6C51E]/35 bg-[#D6C51E]/10 px-3 py-1.5">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-white/55">
          <Clock size={13} className="text-[#D6C51E]" /> Distributes in
        </span>
        <span className="flex items-baseline gap-1">
          <span data-testid="pool-top-depositor-countdown" className="text-sm font-bold tabular-nums text-[#D6C51E]">{fmtCountdown(secsLeft)}</span>
          <span className="text-[11px] font-bold text-white/45">UTC</span>
        </span>
      </div>

      {top.length > 0 && (
        <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-3">
          <span className="text-[11px] text-[#34D07A]">Funded by 10% withdrawal fees · Rank by today's deposits</span>
        </div>
      )}
    </div>
  );
}

function ReqRow({ label, have, need, suffix = "" }) {
  const ok = have >= need;
  return (
    <div className="flex items-center justify-between py-1 text-xs">
      <span className="text-white/60">{label}</span>
      <span className={`flex items-center gap-1.5 font-semibold ${ok ? "text-[#34D07A]" : "text-red-400"}`}>
        {suffix}{have}/{need}{suffix ? "" : ""}
        {ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
      </span>
    </div>
  );
}

function PoolCard({ p, onInfo, onHistory, now }) {
  const est = p.estimate != null ? p.estimate : (p.achievers >= 0 ? p.balance / (p.achievers + 1) : 0);
  const qualified = p.reqs ? p.reqs.every((r) => r.ok) : (p.directsHave >= p.directsNeed && p.capHave >= p.capNeed);
  // Pool shares are AUTO-CREDITED to the withdrawable balance when each cycle closes — no manual claim.
  const claim = p.claim || {};
  const secsLeft = claim.unlock_at ? Math.max(0, Math.floor((new Date(claim.unlock_at).getTime() - now) / 1000)) : (claim.seconds_left ?? null);
  return (
    <div data-testid={`pool-${p.key}`} className="card-glow p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-lg font-bold grad-title">{p.title}</div>
          <div className="text-[11px] text-white/45">{p.period}</div>
        </div>
        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${qualified ? "border-[#0AA84F]/50 bg-[#0AA84F]/15 text-[#34D07A]" : "border-red-500/45 bg-red-500/15 text-red-400"}`}>
          {qualified ? "Qualified" : "Not qualified"}
        </span>
      </div>

      <div className="mt-3 text-[11px] uppercase tracking-wide text-white/45">Current Pool Balance</div>
      <div className="text-3xl font-extrabold text-[#34D07A]">${usd2(p.balance)}</div>

      <div className="mt-3 rounded-xl border border-[#3C6B33]/50 p-3">
        <div className="flex items-center justify-between border-b border-white/5 pb-2 text-sm">
          <span className="text-white/60">Estimated if you qualify</span>
          <span className="font-bold text-[#D6C51E]">${usd2(est)}</span>
        </div>
        <div className="flex items-center justify-between pt-2 text-sm">
          <span className="text-white/60">On-chain achievers</span>
          <span className="font-bold text-[#34D07A]">{p.achievers}</span>
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-[#3C6B33]/50 p-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-bold text-white">Qualification</span>
          <span className={`text-xs font-semibold ${(p.pending ?? 0) > 0 ? "text-red-400" : "text-[#34D07A]"}`}>
            {(p.pending ?? 0) > 0 ? `${p.pending} pending` : "All met"}
          </span>
        </div>
        {p.reqs ? (
          p.reqs.map((r) => {
            const hasNum = typeof r.have === "number" && typeof r.need === "number" && r.need > 0;
            const isText = !!r.text || (!hasNum && (r.have == null));
            const pct = hasNum ? Math.min(100, Math.round((r.have / r.need) * 100)) : (r.ok ? 100 : 0);
            return (
              <div key={r.label} data-testid={`pool-req-${p.key}`} className="py-1.5">
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="text-white/60">{r.label}</span>
                  <span className={`flex items-center gap-1.5 font-semibold ${r.ok ? "text-[#34D07A]" : "text-red-400"}`}>
                    {r.val}
                    {!isText && <span className={`tabular-nums ${r.ok ? "text-[#34D07A]" : "text-white/45"}`}>{pct}%</span>}
                    {r.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                  </span>
                </div>
                {!isText && (
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: r.ok ? "linear-gradient(90deg,#0AA84F,#34D07A)" : "linear-gradient(90deg,#D6C51E,#FFA000)" }}
                    />
                  </div>
                )}
              </div>
            );
          })
        ) : (          <>
            <ReqRow label={p.directsLabel} have={p.directsHave} need={p.directsNeed} />
            <div className="flex items-center justify-between py-1 text-xs">
              <span className="text-white/60">Available mining cap</span>
              <span className="flex items-center gap-1.5 font-semibold text-red-400">
                ${p.capHave}/${p.capNeed} <XCircle size={14} />
              </span>
            </div>
          </>
        )}
      </div>

      <div className="mt-3 space-y-2">
        <div className="flex w-full items-center justify-between rounded-lg border border-[#D6C51E]/35 bg-[#D6C51E]/10 px-3 py-1.5">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-white/55">
            <Clock size={13} className="text-[#D6C51E]" /> Next auto-payout in
          </span>
          <span className="flex items-baseline gap-1">
            <span data-testid={`pool-${p.key}-countdown`} className="text-sm font-bold tabular-nums text-[#D6C51E]">{fmtCountdown(secsLeft)}</span>
            <span className="text-[11px] font-bold text-white/45">UTC</span>
          </span>
        </div>
        <div className="text-[10px] text-white/40">
          Rewards auto-credit to your withdrawable balance every cycle — no claim needed.
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end border-t border-white/5 pt-3">
        <div className="flex items-center gap-2">
          <button
            data-testid={`pool-${p.key}-history`}
            onClick={() => onHistory(p)}
            className="flex items-center gap-1 rounded-lg border border-[#D6C51E]/40 px-2.5 py-1 text-[11px] font-semibold text-[#D6C51E] active:scale-95"
          >
            <History size={12} /> History
          </button>
          <button
            data-testid={`pool-${p.key}-info`}
            onClick={() => onInfo(p)}
            className="info-pulse flex h-6 w-6 items-center justify-center rounded-full border border-[#34D07A]/40 text-[#34D07A] active:scale-95"
          >
            <Info size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PoolsPage() {
  const { me, address } = useWallet();
  const [stats, setStats] = useState(null);
  const [live, setLive] = useState(null); // real per-user pool data
  const [modal, setModal] = useState(null); // {type:'info'|'history', pool}
  const [now, setNow] = useState(Date.now());
  const [topPool, setTopPool] = useState(null);
  useEffect(() => { getDashboardStats().then(setStats).catch(() => {}); }, []);
  useEffect(() => {
    const run = () => getTopDepositors().then(setTopPool).catch(() => {});
    run();
    const t = setInterval(run, 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (address) getPools(address).then(setLive).catch(() => setLive(null));
  }, [address]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const day = Math.floor(Date.now() / 86400000) % 1000;
  const fmtReq = (r) =>
    r.text ? r.text : r.usd ? `$${usd(r.have)}/$${usd(r.need)}` : `${r.have}/${r.need}`;
  const toReqs = (arr) => (arr || []).map((r) => ({ label: r.label, val: fmtReq(r), ok: r.ok, have: r.have, need: r.need, usd: r.usd, text: r.text }));
  const pendingOf = (arr) => (arr || []).filter((r) => !r.ok).length;

  const meta = stats?.pool_meta || {};
  const pools = live
    ? [
        { key: "daily", title: "Daily TITAN Pool", period: `Current on-chain day · ID ${day}`,
          balance: live.daily.balance, achievers: live.daily.achievers, estimate: live.daily.estimate,
          claim: live.daily.claim,
          reqs: toReqs(live.daily.reqs), pending: pendingOf(live.daily.reqs) },
        { key: "weekly", title: "Weekly Champion Pool", period: "Current on-chain week · ID 1",
          balance: live.weekly.balance, achievers: live.weekly.achievers, estimate: live.weekly.estimate,
          claim: live.weekly.claim,
          reqs: toReqs(live.weekly.reqs), pending: pendingOf(live.weekly.reqs) },
        { key: "monthly", title: "Monthly Owner Club Reward", period: "Current on-chain month · ID 1",
          balance: live.monthly.balance, achievers: live.monthly.achievers, estimate: live.monthly.estimate,
          claim: live.monthly.claim,
          reqs: [...toReqs(live.monthly.reqs), { label: "On-chain qualification", val: live.monthly.qualified ? "Submitted" : "Pending", ok: live.monthly.qualified }],
          pending: pendingOf(live.monthly.reqs) + (live.monthly.qualified ? 0 : 1) },
      ]
    : [
        { key: "daily", title: "Daily TITAN Pool", period: `Current on-chain day · ID ${day}`, balance: stats?.pools?.daily_usdt || 0, achievers: meta.daily?.qualified_ids || 0, reqs: [{ label: "Direct with 50+ Stake today", val: "0/1", ok: false }, { label: "Available mining cap", val: "$0/$100", ok: false }], pending: 2 },
        { key: "weekly", title: "Weekly Champion Pool", period: "Current on-chain week · ID 1", balance: stats?.pools?.weekly_usdt || 0, achievers: meta.weekly?.qualified_ids || 0, reqs: [{ label: "Directs with 50+ Stake this week", val: "0/5", ok: false }, { label: "Available mining cap", val: "$0/$200", ok: false }], pending: 2 },
        { key: "monthly", title: "Monthly Owner Club Reward", period: "Current on-chain month · ID 1", balance: stats?.pools?.monthly_usdt || 0, achievers: meta.monthly?.qualified_ids || 0, pending: 8, reqs: [
          { label: "Active membership", val: "Not active", ok: false },
          { label: "Active directs (min $50)", val: "0/10", ok: false },
          { label: "Direct business", val: "$0/$2,000", ok: false },
          { label: "Left qualified IDs", val: "0/25", ok: false },
          { label: "Right qualified IDs", val: "0/25", ok: false },
          { label: "Left matching carry", val: "$0/$5,000", ok: false },
          { label: "Right matching carry", val: "$0/$5,000", ok: false },
          { label: "On-chain qualification", val: "Pending", ok: false },
        ] },
      ];

  const POOL_CAT = { daily: 2, weekly: 3, monthly: 4 };
  return (
    <div data-testid="pools-page" className="flex flex-col gap-4 px-4 pt-4 pb-4">
      <SectionLabel center>Reward Pools</SectionLabel>
      {pools.map((p) => (
        <PoolCard
          key={p.key}
          p={{ ...p, category: POOL_CAT[p.key] }}
          now={now}
          onInfo={(pool) => setModal({ type: "info", pool })}
          onHistory={(pool) => setModal({ type: "history", pool })}
        />
      ))}

      <TopDepositorCard data={topPool} now={now} />

      {modal && createPortal(
        <div data-testid="pool-modal" className="fixed inset-0 z-[200] flex items-center justify-center p-5 bg-black/75 backdrop-blur-sm" onClick={() => setModal(null)}>
          <div className="max-h-[80vh] w-full max-w-[380px] overflow-y-auto rounded-3xl border border-[#3C6B33]/60 bg-[#0A1710] p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-bold grad-title">
                {modal.pool.title} · {modal.type === "info" ? "Rules" : "History"}
              </h3>
              <button data-testid="pool-modal-close" onClick={() => setModal(null)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-white/60">
                <X size={16} />
              </button>
            </div>
            {modal.type === "info" ? (
              <>
                <div className="mb-3 grid grid-cols-2 gap-2">
                  <div className="rounded-xl border border-[#3C6B33]/50 p-3">
                    <div className="text-[10px] uppercase text-white/45">Pool Balance</div>
                    <div className="text-lg font-bold text-[#34D07A]">${usd2(modal.pool.balance)}</div>
                  </div>
                  <div className="rounded-xl border border-[#3C6B33]/50 p-3">
                    <div className="text-[10px] uppercase text-white/45">Your Est. Reward</div>
                    <div className="text-lg font-bold text-[#D6C51E]">${usd2(modal.pool.estimate != null ? modal.pool.estimate : modal.pool.balance / (modal.pool.achievers + 1))}</div>
                  </div>
                </div>
                <div className="mb-3 flex items-center justify-between rounded-xl border border-[#3C6B33]/50 p-3">
                  <span className="text-xs text-white/60">On-chain qualified users</span>
                  <span data-testid="pool-modal-achievers" className="text-sm font-bold text-[#34D07A]">{modal.pool.achievers}</span>
                </div>
                <div className="mb-3 flex items-center justify-between rounded-xl border border-[#3C6B33]/50 p-3">
                  <span className="text-xs text-white/60">Are you qualified?</span>
                  {(modal.pool.reqs ? modal.pool.reqs.every((r) => r.ok) : (modal.pool.directsHave >= modal.pool.directsNeed && modal.pool.capHave >= modal.pool.capNeed)) ? (
                    <span className="flex items-center gap-1 text-xs font-bold text-[#34D07A]"><CheckCircle2 size={14} /> Qualified</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs font-bold text-red-400"><XCircle size={14} /> Not qualified</span>
                  )}
                </div>
                {/* Live per-requirement checklist for THIS pool */}
                <div className="mb-3 rounded-xl border border-[#3C6B33]/50 p-3">
                  {(modal.pool.reqs || []).map((r) => (
                    <div key={r.label} className="flex items-center justify-between py-1 text-xs">
                      <span className="text-white/60">{r.label}</span>
                      <span className={`flex items-center gap-1.5 font-semibold ${r.ok ? "text-[#34D07A]" : "text-red-400"}`}>
                        {r.val} {r.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                      </span>
                    </div>
                  ))}
                </div>
                <ul className="space-y-2 text-xs text-white/70">
                  <li>• Share = pool balance split equally among all on-chain achievers.</li>
                  {modal.pool.key === "monthly" ? (
                    <li>• Funded by a <b className="text-[#D6C51E]">10%</b> deduction from all Direct, Level, Daily &amp; Weekly rewards across the network.</li>
                  ) : (
                    <li>• A <b className="text-[#D6C51E]">10%</b> deduction funds the Monthly Owner Club pool — you receive the net <b className="text-[#34D07A]">90%</b>.</li>
                  )}
                  <li>• Reward is credited in USDT to your account balance.</li>
                  <li>• Estimate is live; final amount locks when the pool closes at 00:00 UTC.</li>
                </ul>
              </>
            ) : (
              <div className="py-6 text-center text-xs text-white/45">
                No confirmed {modal.pool.title.toLowerCase()} claims found for this wallet.
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
